import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { motion } from "framer-motion";
import { CalendarClock, MapPin, ExternalLink, TrendingUp, Ticket, Music, Trophy, Theater, Palette, Building2, PartyPopper, Users2, Sparkles, Info } from "lucide-react";

const CAT_META = {
  concert: { icon: Music, color: "#8b5cf6" },
  sports: { icon: Trophy, color: "#f97316" },
  theatre: { icon: Theater, color: "#ec4899" },
  arts: { icon: Palette, color: "#14b8a6" },
  expo: { icon: Building2, color: "#3b82f6" },
  conference: { icon: Users2, color: "#0ea5e9" },
  festival: { icon: PartyPopper, color: "#eab308" },
  family: { icon: Users2, color: "#22c55e" },
  other: { icon: Ticket, color: "#64748b" },
};

const impactColor = (lvl) => (lvl === "high" ? "var(--demand-high)" : lvl === "medium" ? "var(--demand-medium)" : "var(--demand-low)");

function ProviderChip({ p, t }) {
  const [showNote, setShowNote] = useState(false);
  const { lang } = useApp();
  return (
    <span className="relative">
      <button
        type="button"
        data-testid={`event-provider-${p.key}`}
        onClick={() => p.note && setShowNote((s) => !s)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors"
        style={{
          borderColor: p.available ? "color-mix(in srgb, var(--demand-low) 45%, transparent)" : "var(--border)",
          backgroundColor: p.available ? "color-mix(in srgb, var(--demand-low) 12%, transparent)" : "transparent",
          color: p.available ? "var(--demand-low)" : "var(--muted-foreground)",
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.available ? "var(--demand-low)" : "var(--muted-foreground)" }} />
        {p.label}
        {!p.available && ` · ${t("providerUnavailable")}`}
        {p.note && <Info className="w-3 h-3 opacity-60" />}
      </button>
      {showNote && p.note && (
        <span className="absolute z-20 top-full left-0 mt-1 w-64 text-xs bg-popover border border-border rounded-lg p-2.5 shadow-lg text-muted-foreground">
          {lang === "it" ? p.note.it : p.note.en}
        </span>
      )}
    </span>
  );
}

export default function EventsPanel({ propId }) {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [radius, setRadius] = useState(30);
  const [cat, setCat] = useState("all");

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/events?days=90&radius_km=${r}`);
      setData(data);
      setRadius(data.radius_km);
    } catch { setData(null); }
    setLoading(false);
  }, [propId]);

  useEffect(() => { if (propId) load(radius); }, [propId]); // eslint-disable-line

  const changeRadius = (r) => { setRadius(r); load(r); };

  const events = data?.events || [];
  const filtered = cat === "all" ? events : events.filter((e) => e.category_key === cat);
  const cats = Array.from(new Set(events.map((e) => e.category_key)));
  const fmtDate = (d) => new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "short", day: "numeric", month: "short" });
  const impactLabel = (lvl) => (lvl === "high" ? t("impactHigh") : lvl === "medium" ? t("impactMedium") : t("impactLow"));

  return (
    <motion.div
      data-testid="events-panel"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold leading-tight">{t("relevantEvents")}</h3>
            <p className="text-sm text-muted-foreground">{t("eventsSubtitle")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mr-1">{t("eventRadius")}</span>
          {[10, 20, 30, 50].map((r) => (
            <button key={r} data-testid={`event-radius-${r}`} onClick={() => changeRadius(r)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${radius === r ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>
              {r} km
            </button>
          ))}
        </div>
      </div>

      {/* provider status + summary */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mr-1">{t("eventSources")}:</span>
        {(data?.providers || []).map((p) => <ProviderChip key={p.key} p={p} t={t} />)}
        {!loading && (
          <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            <span className="font-data font-semibold text-foreground">{events.length}</span> {t("eventsFound")}
            {data?.high_impact > 0 && (
              <span className="flex items-center gap-1" style={{ color: "var(--demand-high)" }}>
                <TrendingUp className="w-3.5 h-3.5" /><span className="font-data font-semibold">{data.high_impact}</span> {t("highImpactEvents")}
              </span>
            )}
          </span>
        )}
      </div>

      {/* category filter */}
      {!loading && cats.length >= 1 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button data-testid="event-cat-all" onClick={() => setCat("all")}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${cat === "all" ? "bg-secondary border-primary/40" : "border-border hover:bg-accent"}`}>
            {t("allCategories")} ({events.length})
          </button>
          {cats.map((c) => {
            const meta = CAT_META[c] || CAT_META.other;
            const Icon = meta.icon;
            const label = events.find((e) => e.category_key === c)?.[lang === "it" ? "category_it" : "category_en"] || c;
            const n = events.filter((e) => e.category_key === c).length;
            return (
              <button key={c} data-testid={`event-cat-${c}`} onClick={() => setCat(c)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${cat === c ? "bg-secondary border-primary/40" : "border-border hover:bg-accent"}`}>
                <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />{label} ({n})
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground py-10 text-center flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4 animate-pulse text-primary" /> {t("loadingEvents")}
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">{t("noEventsFound")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1" data-testid="events-list">
          {filtered.map((e, i) => {
            const meta = CAT_META[e.category_key] || CAT_META.other;
            const Icon = meta.icon;
            const label = lang === "it" ? e.category_it : e.category_en;
            return (
              <div key={i} data-testid={`event-card-${i}`}
                className="group border border-border rounded-lg p-3.5 hover:border-primary/40 hover:-translate-y-0.5 transition-all bg-secondary/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `color-mix(in srgb, ${meta.color} 15%, transparent)`, color: meta.color }}>
                    <Icon className="w-3 h-3" />{label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded"
                    title={t("estImpact")}
                    style={{ backgroundColor: `color-mix(in srgb, ${impactColor(e.impact_level)} 18%, transparent)`, color: impactColor(e.impact_level) }}>
                    <TrendingUp className="w-3 h-3" />+{e.impact} · {impactLabel(e.impact_level)}
                  </span>
                </div>
                <p className="text-sm font-semibold leading-snug line-clamp-2 mb-2">{e.name}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />{fmtDate(e.date)}</span>
                  {e.distance_km != null && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /><span className="font-data">{e.distance_km}</span> km</span>}
                </div>
                {(e.venue || e.city) && <p className="text-xs text-muted-foreground mt-1 truncate">{[e.venue, e.city].filter(Boolean).join(" · ")}</p>}
                {e.url && (
                  <a href={e.url} target="_blank" rel="noopener noreferrer" data-testid={`event-link-${i}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary mt-2 hover:underline">
                    {t("viewEvent")} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
